import React from "react";
import { Skeleton, Stack } from "@mui/material";

import { Partner, PartnerResource } from "../../../../api/partners";
import { DetailField } from "./DetailField";

interface Props {
  gcalEventId: string;
    /**
   * The scope as AUTHORED on the calendar event (metadata.partnerId), not as
   * projected into DynamoDB. Those differ for the minutes between a save and
   * the next sync pass, and showing the projection would make a just-saved
   * change look like it did not take.
   */
  partnerId?: string;
  recurringMeetingId?: string;
  refreshKey?: number;
}

/**
 * Read-only summary of a meeting's partner association for the details view:
 * "Private — {partner}" (partner-scoped), "Preferred — {partner}" (differentiated
 * card), or "None" — so an admin can see it without opening the edit dialog.
 */
export const PartnerAssociationReadOnly: React.FC<Props> = ({
  gcalEventId,
  partnerId,
  recurringMeetingId,
  refreshKey,
}) => {
  const stableKey = recurringMeetingId ?? gcalEventId;

  const [loading, setLoading] = React.useState(true);
  const [value, setValue] = React.useState("None");

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);

    // Only the partner list is fetched — the scope arrives as a prop, read
    // from the calendar metadata the admin authored.
    const pid = partnerId?.trim() || null;
    PartnerResource.getListCached()
      .catch(() => [] as Partner[])
      .then((partners) => {
        if (cancelled) return;
        if (pid) {
          const name =
            partners.find((p) => p.id === pid)?.displayName ??
            "Unknown partner";
          setValue(`Private — ${name}`);
          return;
        }
        // Preferred is one-to-many: list EVERY partner that marked it.
        const owners = partners.filter((p) =>
          p.preferredSessionIds?.includes(stableKey),
        );
        setValue(
          owners.length
            ? `Preferred — ${owners.map((o) => o.displayName).join(", ")}`
            : "None",
        );
      })
      .catch(() => {
        if (!cancelled) setValue("—");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [partnerId, stableKey, refreshKey]);

  if (loading) {
    return <Skeleton variant="text" width={200} height={24} />;
  }

  return (
    <Stack direction="row" gap={2} alignItems="center">
      <DetailField field="Partner Access" value={value} />
    </Stack>
  );
};
